/* eslint max-lines: 0 */
import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {
    Button,
    Spinner,
} from 'react-bootstrap';
import * as THREE from 'three';
import {
        Icon,
} from '@iconify/react';
import cancelIcon from '@iconify/icons-mdi/cancel';
import createLinkedRoomIcon from '@iconify/icons-mdi/link-variant-plus';
import createInfopointIcon from '@iconify/icons-mdi/chat-plus-outline';
import showDefaultRotationIcon from '@iconify/icons-mdi/bullseye';
import setDefaultRotationIcon from '@iconify/icons-mdi/bullseye-arrow';
import actions from "@shared/actions";
import {
    icons,
    getIconImg,
} from "../icons";
import {
    language,
} from "@shared/language";

const i18n = {
    de: {
        infopoint_title: 'Infopunkt',
        linkpoint_title: 'Sprungpunkt',
        edit_point_title: 'bearbeiten...',
        create_point_title: 'anlegen...',

        point_form_title_label: 'Bezeichnung',
        point_form_title_placeholder: 'Text unter dem Symbol',

        point_form_text_label: 'Anzeigetext',
        point_form_text_placeholder: 'Anzeigetext beim Anklicken des Punktes',

        delete_point_accept_label: 'Löschen',
        create_point_accept_label: 'Anlegen',
        edit_point_accept_label: 'Speichern',

        no_floorplan_message: 'Es wurde noch kein Grundriss hinterlegt',
        no_image_message: 'Es wurde noch kein Bild hinterlegt',

        controls_set_start_rotation_label: 'als Startansicht festlegen',

        controls_goto_start_rotation_label: 'zeige Startansicht',

        controls_create_linked_room_label: 'Sprungpunkt setzen',

        controls_create_infopoint_label: 'Infopunkt setzen',
    },
    en: {
        infopoint_title: 'Info point',
        linkpoint_title: 'Jump point',
        edit_point_title: 'edit...',
        create_point_title: 'create...',

        point_form_title_label: 'Description',
        point_form_title_placeholder: 'Text under the symbol',

        point_form_text_label: 'Display text',
        point_form_text_placeholder: 'Display text when clicking on the point',

        delete_point_accept_label: 'Delete',
        create_point_accept_label: 'Create',
        edit_point_accept_label: 'Save',

        no_floorplan_message: 'No floor plan has been saved yet',
        no_image_message: 'No picture has been added yet',

        controls_set_start_rotation_label: 'set as start view',

        controls_goto_start_rotation_label: 'show start view',

        controls_create_linked_room_label: 'Set jump point',

        controls_create_infopoint_label: 'Set info point',
    },
};

export class EntityScene extends React.Component {
    static propTypes = {
        entity: ImmutablePropTypes.map,
        autoPlay: PropTypes.bool,
        readOnly: PropTypes.bool,
        onSelectRoom: PropTypes.func,
        onShowInfo: PropTypes.func,
        bottom: PropTypes.string,
        fixBottom: PropTypes.bool,
        width: PropTypes.number,
        height: PropTypes.number,
        primaryColor: PropTypes.string,
        secondaryColor: PropTypes.string,

        // store
        rooms: ImmutablePropTypes.list,
        onRoomEditAction: PropTypes.func,
        onInfopointEditAction: PropTypes.func,
        onShowStandardDialog: PropTypes.func,
    }

    static defaultProps = {
        autoPlay: true,
        readOnly: false,
        fixBottom: true,
        onSelectRoom: undefined,
        primaryColor: '#009245',
        secondaryColor: '#ffffff',
    };

    static scrollHeight = (function() {
        const probe = document.createElement('div');
        probe.style.display = 'none';
        probe.style.fontSize = 'initial';
        document.body.appendChild(probe);
        const fontSize = getComputedStyle(probe).fontSize;
        document.body.removeChild(probe);
        return (fontSize ? parseInt(fontSize) : 20) * window.devicePixelRatio;
    }());

    state = {
        loadState: 'none',
        placement: false,
    }

    constructor(props, type) {
        super(props);
        this.type = type;
        this.isRoom = type === 'room';
        this.isFloor = type === 'floor';
        this.renderRef = React.createRef();
        this.frameId = null;
        this.invalid = true;
        this.dragInfo = undefined;
        this.textureCache = {};
    }

    componentDidMount() {
        const width = this.props.width;
        const height = this.props.height;

        this.raycaster = new THREE.Raycaster();

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color('#ffffff');

        this.stage = new THREE.Group();
        this.stage.position.z = -800;
        this.scene.add(this.stage);

        if (this.isRoom) {
            this.camera = new THREE.PerspectiveCamera(75, width / height, 1, 2000);
            this.backgroundGeometry = new THREE.SphereBufferGeometry(1000, 60, 40).scale(-1, 1, 1);
            this.bottomMaterial = new THREE.MeshBasicMaterial({
                side: THREE.FrontSide,
                transparent: true,
            });
            const bottomMesh = this.bottomMesh = new THREE.Mesh(
                new THREE.PlaneGeometry(550, 550),
                this.bottomMaterial,
            );
            bottomMesh.position.y = -900;
            bottomMesh.rotation.x = -Math.PI / 2.0;
            this.stage.add(bottomMesh);
        } else {
            this.camera = new THREE.OrthographicCamera(width / -2, width / 2, height / 2, height / -2, 1, 2000);
            this.backgroundGeometry = new THREE.PlaneGeometry(1, 1);
        }
        this.backgroundMaterial = new THREE.MeshBasicMaterial({side: THREE.DoubleSide});
        this.backgroundMesh = new THREE.Mesh(this.backgroundGeometry, this.backgroundMaterial);
        this.stage.add(this.backgroundMesh);

        this.markersGroup = new THREE.Group();
        this.stage.add(this.markersGroup);

        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(width, height);
        this.renderer.setClearColor('#cccccc');
        this.renderer.shadowMap.enabled = this.isFloor;
        if (this.isFloor) {this.renderer.shadowMap.type = THREE.PCFSoftShadowMap}
        this.renderRef.current.appendChild(this.renderer.domElement);

        window.addEventListener('resize', this.onResize, false);
        document.addEventListener('touchmove', this.onPointerMove, false);
        document.addEventListener('touchend', this.onPointerUp, false);
        document.addEventListener('mousemove', this.onPointerMove, false);
        document.addEventListener('mouseup', this.onPointerUp, false);
        this.renderer.domElement.addEventListener('wheel', this.onWheel, false);
        this.renderer.domElement.addEventListener('touchstart', this.onPointerDown, false);
        this.renderer.domElement.addEventListener('mousedown', this.onPointerDown, false);

        this.loadBackgroundImage();
        if (this.isRoom) {
            this.loadBottomImage();
            this.gotoStartRotation();
        }
    }

    componentWillUnmount() {
        this.stop();
        if (this.renderer) {this.renderer.dispose()}
        window.removeEventListener('resize', this.onResize, false);
        document.removeEventListener('touchmove', this.onPointerMove, false);
        document.removeEventListener('touchend', this.onPointerUp, false);
        document.removeEventListener('mousemove', this.onPointerMove, false);
        document.removeEventListener('mouseup', this.onPointerUp, false);
        if (this.renderer && this.renderer.domElement) {
            this.renderer.domElement.removeEventListener('wheel', this.onWheel, false);
            this.renderer.domElement.removeEventListener('touchstart', this.onPointerDown, false);
            this.renderer.domElement.removeEventListener('mousedown', this.onPointerDown, false);
            this.renderRef.current.removeChild(this.renderer.domElement);
        }
    }

    componentDidUpdate(oldProps) {
        if (this.getBackgroundSrc() !== this.getBackgroundSrc(oldProps)) {
            this.loadBackgroundImage();
        }
        if (this.getBottomSrc() !== this.getBottomSrc(oldProps)) {
            this.loadBottomImage();
        }
        if (this.props.width !== oldProps.width || this.props.height !== oldProps.height) {
            this.onResize();
        }
        if (this.props.entity.get('infopoints') !== oldProps.entity.get('infopoints')) {
            this.refreshMarkers();
        }
        if (this.isRoom && this.props.entity.get('start_rotation') !== oldProps.entity.get('start_rotation')) {
            this.gotoStartRotation();
        }
    }

    getStartRotation() {
        return (this.isRoom && this.props && this.props.entity) ? this.props.entity.get('start_rotation') : 0;
    }

    setStartRotation() {
        if (this.isRoom && this.props && this.props.entity) {
            this.props.onRoomEditAction({
                room_id: this.props.entity.get('id'),
                start_rotation: this.stage.rotation.y % (2 * Math.PI),
            });
        }
    }

    isStartRotation() {
        return Math.abs((this.getStartRotation() - (this.stage.rotation.y % (2 * Math.PI)))) < Math.PI / 20;
    }

    gotoStartRotation() {
        this.stage.rotation.y = this.getStartRotation();
        this.forceUpdate();
        this.invalid = true;
    }

    updateMarkerMatrix = (marker) => {
        const uv = marker.currentUV;
        if (this.isRoom) {
            const zoom = (this.stage.position.z + 800) / 1600;
            const lng = (-uv.x * 2 - 0.5) * Math.PI;
            const lat = (-1 / 2 + uv.y) * Math.PI;
            const translate = new THREE.Matrix4();
            translate.makeTranslation(0, 0, -900);
            const scale = new THREE.Matrix4();
            const factor = 1.1 - zoom;
            scale.makeScale(factor * 6, factor * 6, 1);
            const rotateX = new THREE.Matrix4();
            rotateX.makeRotationX(lat);
            const rotateY = new THREE.Matrix4();
            rotateY.makeRotationY(lng);
            marker.matrix.identity();
            marker.matrix.premultiply(scale);
            marker.matrix.premultiply(translate);
            marker.matrix.premultiply(rotateX);
            marker.matrix.premultiply(rotateY);
        } else {
            const zoom = (this.camera.right * 2) / this.props.width;
            const translate = new THREE.Matrix4();
            const width = this.backgroundMesh.scale.x;
            const height = this.backgroundMesh.scale.y;
            translate.makeTranslation(-width / 2 + uv.x * width, -height / 2 + uv.y * height, 2);
            const scale = new THREE.Matrix4();
            scale.makeScale(zoom, zoom, 1);
            marker.matrix.identity();
            marker.matrix.premultiply(scale);
            marker.matrix.premultiply(translate);
        }
    }

    updateMarkerMatrices = () => {
        for (const marker of Object.values(this.markersGroup.children)) {
            this.updateMarkerMatrix(marker);
        }
    }

    refreshMarkers() {
        this.stage.remove(this.markersGroup);
        this.markersGroup = new THREE.Group();
        this.stage.add(this.markersGroup);
        for (const infopoint of this.props.entity.get('infopoints')) {
            const room = this.getLinkedRoom(infopoint);
            this.addMarker(
                infopoint.get('id'),
                room ? 'link' : 'infopoint',
                new THREE.Vector2(infopoint.get('u') % 1, infopoint.get('v') % 1),
                room ? room.get('name') : infopoint.get('title'),
                infopoint.get('symbol') || 'passway',
                !this.props.readOnly,
            );
        }
        this.highlightMarker();
        this.invalid = true;
    }

    getLinkedRoom = (infopoint) => {
        const room_id = infopoint.get('linked_room_id');
        if (!room_id) {
            return undefined;
        }
        return this.props.rooms.find(room => room.get('id') === room_id);
    }

    getInfopointById = (infopoint_id) => {
        return this.props.entity.get('infopoints').find(infopoint => infopoint.get('id') === infopoint_id);
    }

    getMarkerInfopoint = (marker) => {
        return this.getInfopointById(marker.name);
    }

    flatMesh = (texture) => {
        return new THREE.Mesh(
            new THREE.PlaneGeometry(texture.image.width * 0.1, texture.image.height * 0.1),
            new THREE.MeshBasicMaterial({
                side: THREE.DoubleSide,
                map: texture,
                transparent: true,
                opacity: 1.0,
            }),
        );
    }

    addMarker = (id, design, uv, label, symbolParam, withButton) => { // eslint-disable-line max-params
        const marker = new THREE.Group();
        const symbol = symbolParam || (design === 'link' ? 'passway' : 'info');
        const pin = this.flatMesh(this.getSymbolTexture(symbol, design));
        pin.name = 'pin';
        marker.add(pin);

        if (label) {
            const text = this.flatMesh(this.getLabelTexture(label, design));
            text.translateY(design === 'link' ? -26 : -22);
            text.name = 'text';
            marker.add(text);
        }

        if (withButton) {
            const button = this.flatMesh(this.getButtonTexture(design));
            button.translateX(20);
            button.translateY(20);
            button.translateZ(1);
            button.name = 'button';
            button.visible = false;
            marker.add(button);
        }

        marker.name = id;
        marker.highlighted = false;
        marker.matrixAutoUpdate = false;
        marker.currentUV = uv || new THREE.Vector2(1, 1);
        this.updateMarkerMatrix(marker);
        this.markersGroup.add(marker);
        return marker;
    }

    highlightMarker = (marker, highlight) => {
        for (const currentMarker of this.markersGroup.children) {
            const high = ((marker === currentMarker) && highlight);
            if (high !== currentMarker.highlighted) {
                const pin = currentMarker.getObjectByName('pin');
                const text = currentMarker.getObjectByName('text');
                const button = currentMarker.getObjectByName('button');
                const scale = high ? 1.2 : 1.0;
                pin.scale.x = pin.scale.y = scale;
                if (text) {
                    text.scale.x = text.scale.y = scale;
                }
                if (button) {
                    button.visible = high;
                }
                currentMarker.highlighted = high;
                this.invalid = true;
            }
        }
    }

    getForegroundColor = () => {
        return this.props.primaryColor;
    }

    getBackgroundColor = (design, alpha) => {
        const alphaStr = (alpha === undefined ? '' : ', ' + alpha);
        const primaryColor = this.hexToRgb(this.props.primaryColor);
        const secondaryColor = this.hexToRgb(this.props.secondaryColor);
        return design === 'link'
            ? 'rgba(' + primaryColor.r + ', ' + primaryColor.g + ', ' + primaryColor.b + ' ' + alphaStr + ')'
            : 'rgba(' + secondaryColor.r + ', ' + secondaryColor.g + ', ' + secondaryColor.b + ' ' + alphaStr + ') ';
    }

    hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
        } : null;
    }

    getIconTexture = (icon, color, size) => {
        const key = 'ICON-' + this.isRoom + '-' + icon.name;
        let texture = this.textureCache[key];
        if (texture) {
            return texture;
        }
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        texture = this.textureCache[key] = new THREE.CanvasTexture(canvas);
        const img = getIconImg(icon, size, color, 0.5);
        const draw = () => {
            const context = canvas.getContext('2d');
            context.clearRect(0, 0, size, size);
            const scale = size / icon.size;
            context.scale(scale, scale);
            context.drawImage(img, 0, 0);
            texture.needsUpdate = true;
            this.invalid = true;
        };
        if (img.complete) {
            draw();
        } else {
            img.onload = draw;
        }
        return texture;
    }

    getSymbolTexture = (symbol, design) => {
        return this.getIconTexture(
            icons[symbol],
            this.getForegroundColor(design, 0.5),
            design === 'link' ? 320 : 240,
        );
    }

    getButtonTexture = (design) => {
        return this.getIconTexture(
            icons['settings'],
            this.getForegroundColor(design, 0.5),
            160,
        );
    }

    drawRoundedRect = (context, x, y, w, h, rParam) => { // eslint-disable-line max-params
        let r = w < 2 * rParam ? w / 2 : rParam;
        if (h < 2 * r) {
            r = h / 2;
        }
        context.beginPath();
        context.moveTo(x + r, y);
        context.arcTo(x + w, y, x + w, y + h, r);
        context.arcTo(x + w, y + h, x, y + h, r);
        context.arcTo(x, y + h, x, y, r);
        context.arcTo(x, y, x + w, y, r);
        context.closePath();
    }

    wrapText = (context, text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = words.shift();
        let currentWidth = context.measureText(currentLine).width;
        for (const word of words) {
            const width = context.measureText(currentLine + ' ' + word).width;
            if (width < maxWidth) {
                currentLine += ' ' + word;
                currentWidth = width;
            } else {
                lines.push([currentLine, currentWidth]);
                currentLine = word;
                currentWidth = context.measureText(currentLine).width;
            }
        }
        lines.push([currentLine, currentWidth]);
        return lines;
    }

    getLabelTexture = (text, design) => {
        const key = 'LABEL-' + text + '-' + design;
        let texture = this.textureCache[key];
        if (texture) {
            return texture;
        }

        const border = 32;
        const lineHeight = design === 'link' ? 100 : 72;
        const fontMode = 'bold';
        const fontFamily = 'helvetica';
        const font = fontMode + ' ' + lineHeight + 'px ' + fontFamily;

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        let lineWidth = 1000;
        context.font = font;
        const lines = this.wrapText(context, text, lineWidth);
        lineWidth = lines.reduce((w, line) => line[1] > w ? line[1] : w, 0);
        const width = lineWidth + 4 * border;
        const height = lines.length * lineHeight + 2 * border;
        canvas.width = width;
        canvas.height = height;
        context.clearRect(0, 0, width, height);

        context.fillStyle = this.getBackgroundColor(design, 0.4);
        this.drawRoundedRect(context, 0, 0, width, height, border + lineHeight / 2);
        context.fill();

        context.font = font;
        //context.fillStyle = design === "link" ? '#fff' : '#009245';
        context.fillStyle = design === "link" ? this.props.secondaryColor : this.props.primaryColor;
        context.textBaseline = 'middle';
        for (const [index, line] of Object.entries(lines)) {
            const [lineText, lineWidth] = line;
            context.fillText(lineText, width / 2 - lineWidth / 2, (Number(index) + 0.5) * lineHeight + border);
        }
        texture = this.textureCache[key] = new THREE.CanvasTexture(canvas);
        return texture;
    }

    getBackgroundSrc = (props) => {
        const entity = (props || this.props).entity;
        if (entity === undefined) {
            return undefined;
        }
        const tour_id = window.tour_editor.tour_id;
        const kind = this.isRoom ? 'panorama' : 'floorplan';
        const background = entity.get(kind);
        if (background === undefined || background === null || background.length === 0) {
            return undefined;
        }
        return `/tour/${tour_id}/${kind}/${background}`;
    }

    loadBackgroundImage = () => {
        const backgroundSrc = this.getBackgroundSrc();
        if (backgroundSrc !== undefined) {
            this.setState({loadState: 'loading'});
            this.backgroundMaterial.map = new THREE.TextureLoader().load(backgroundSrc, () => {
                this.backgroundMaterial.needsUpdate = true;
                this.setState({loadState: 'loaded'});
                this.onResize();
                this.refreshMarkers();
                this.start();
            });
        } else {
            this.setState({loadState: 'none'});
            this.backgroundMaterial.map = undefined;
            this.onResize();
        }
        this.backgroundMaterial.needsUpdate = true;
    }

    getBottomSrc = (props) => {
        return (props || this.props).bottom || undefined;
    }

    drawBottomImage = (bottomSrc) => {
        const key = 'BOTTOM-' + bottomSrc;
        let texture = this.textureCache[key];
        if (texture) {
            return texture;
        }
        const size = 1024;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = canvas.height = size;
        context.clearRect(0, 0, size, size);
        texture = this.textureCache[key] = new THREE.CanvasTexture(canvas);
        const img = document.createElement('img');
        img.src = bottomSrc;
        const draw = () => {
            const innerSize = size * 0.75;
            const half = size / 2;
            const gradient = context.createRadialGradient(half, half, innerSize / 2, half, half, half);
            //gradient.addColorStop(0, 'rgba(255,255,255,1)');
            //gradient.addColorStop(0.1, 'rgba(0,0,0,0.8)');
            //gradient.addColorStop(0.6, 'rgba(255,255,255,0.4)');
            //gradient.addColorStop(1, 'rgba(0,0,0,0)');

            gradient.addColorStop(0, 'rgba(255,255,255,1)');
            //gradient.addColorStop(0, 'rgba(255,255,255,0.7)');
            gradient.addColorStop(0.01, 'rgba(88,88,88,0.3)');
            //gradient.addColorStop(0.012, 'rgba(0,0,0,0.2)');
            //gradient.addColorStop(0.1, 'rgba(255,255,255,0.3)');
            //gradient.addColorStop(0.02, 'rgba(255,255,255,0.3)');
            //gradient.addColorStop(0.95, 'rgba(0,0,0,0.8)');
            gradient.addColorStop(0.1, 'rgba(255,255,255,0.3)');
            gradient.addColorStop(0.5, 'rgba(255,255,255,0.3)');
            gradient.addColorStop(0.501, 'rgba(88,88,88,0.2)');
            gradient.addColorStop(0.502, 'rgba(255,255,255,0)');
            gradient.addColorStop(1, 'rgba(255,255,255,0)');
            //gradient.addColorStop(0.8, 'rgba(255,255,255,0)');
            context.fillStyle = gradient;
            context.fillRect(0, 0, size, size);
            context.beginPath();
            context.arc(size / 2, size / 2, innerSize / 2, 0, Math.PI * 2);
            context.clip();
            const scale = innerSize / img.width;
            context.translate(size / 2, size / 2);
            context.scale(scale, scale);
            context.drawImage(img, -img.width / 2, -img.height / 2);
            texture.needsUpdate = true;
            this.invalid = true;
        };
        if (img.complete) {
            draw();
        } else {
            img.onload = draw;
        }
        return texture;
    }

    loadBottomImage = () => {
        const bottomSrc = this.getBottomSrc();
        this.bottomMesh.visible = true;
        this.bottomMaterial.map = this.drawBottomImage(
            bottomSrc !== undefined ? bottomSrc : '/app/images/avatar/avatar_placeholder.jpg',
        );
        this.bottomMaterial.needsUpdate = true;
        this.invalid = true;
        /*if (bottomSrc !== undefined) {
        } else {
            this.bottomMesh.visible = false;
        }*/
    }

    onResize = () => {
        const width = this.props.width;
        const height = this.props.height;
        if (this.isRoom) {
            this.camera.aspect = width / height;
        } else {
            const image = this.backgroundMaterial.map && this.backgroundMaterial.map.image;
            const width = image ? image.width : this.props.width;
            const height = image ? image.height : this.props.height;
            this.backgroundMesh.scale.set(width, height, 1);
            const scale = Math.min(this.props.width / width, this.props.height / height);
            const cameraWidth = this.props.width / scale;
            const cameraHeight = this.props.height / scale;
            this.camera.left = cameraWidth / -2;
            this.camera.right = cameraWidth / 2;
            this.camera.top = cameraHeight / 2;
            this.camera.bottom = cameraHeight / -2;
            this.stage.position.x = this.stage.position.y = 0;
            this.updateMarkerMatrices();
        }
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
        this.invalid = true;
    }

    getMousePoint = (ev) => {
        return new THREE.Vector2(ev.clientX ?? ev.touches[0].clientX, ev.clientY ?? ev.touches[0].clientY);
    }

    getRayIntersects = (point) => {
        this.raycaster.setFromCamera(new THREE.Vector2((point.x * 2) - 1, -(point.y * 2) + 1), this.camera);
        return this.raycaster.intersectObjects(this.scene.children, true);
    }

    hitTest = (mousePoint) => {
        const rect = this.renderer.domElement.getBoundingClientRect();
        const intersects = this.getRayIntersects(
            new THREE.Vector2((mousePoint.x - rect.left) / rect.width, (mousePoint.y - rect.top) / rect.height),
        );
        let marker = false;
        let uv = new THREE.Vector2(-1, -1);
        let name = undefined;
        for (const intersected of intersects) {
            if (!marker && intersected.object && ['pin', 'text', 'button'].includes(intersected.object.name)) {
                marker = intersected.object.parent;
                name = intersected.object.name;
            } else if (intersected.object === this.backgroundMesh) {
                uv = intersected.uv;
            }
        }
        return {
            marker,
            name,
            uv,
        };
    }

    onWheel = (ev) => {
        ev.preventDefault();
        const deltaY = ev.deltaY * [1, EntityScene.scrollHeight, window.innerHeight][ev.deltaMode];
        if (this.isRoom) {
            this.stage.position.z += deltaY * -0.6;
            this.stage.position.z = Math.min(Math.max(this.stage.position.z, -800), 800);
        } else {
            const zoom = (this.camera.right * 2) / this.props.width;
            const factor = Math.max(Math.min(zoom * (1 + deltaY * 0.002), 100), 0.1);
            const width = this.props.width * factor;
            const height = this.props.height * factor;
            this.camera.left = width / -2;
            this.camera.right = width / 2;
            this.camera.top = height / 2;
            this.camera.bottom = height / -2;
            this.camera.updateProjectionMatrix();
            if (factor > 1) {
                this.stage.position.x = this.stage.position.y = 0;
            }
        }
        this.updateMarkerMatrices();
        this.invalid = true;
    }

    onPointerDown = (ev) => {
        const mousePoint = this.getMousePoint(ev);
        const {
            marker,
            name,
            uv,
        } = this.hitTest(mousePoint);
        if (this.dragInfo &&
            this.dragInfo.marker &&
            ['infopoint', 'link'].includes(this.dragInfo.marker.name)
        ) {
            this.onInfopointCRUD(this.dragInfo.marker.name, uv.x, uv.y);
            this.markersGroup.remove(this.dragInfo.marker);
            this.dragInfo = undefined;
            this.setState({placement: false});
        } else {
            this.dragInfo = {
                name: name,
                marker: marker,
                startMouse: mousePoint,
            };
            if (marker && !this.props.readOnly) {
                this.dragInfo.startState = this.dragInfo.state = uv;
            } else if (this.props.readOnly || !marker) {
                if (this.isRoom) {
                    this.dragInfo.startState = this.dragInfo.state = new THREE.Vector2(this.stage.rotation.x, this.stage.rotation.y);
                } else {
                    this.dragInfo.startState = this.dragInfo.state = new THREE.Vector2(this.stage.position.x, this.stage.position.y);
                }
            }
        }
        this.invalid = true;
    }

    onPointerUp = (ev) => {
        if (!['path', 'svg', 'BUTTON'].includes(ev.target.tagName)) {
            if (this.dragInfo &&
                this.dragInfo.marker &&
                ['infopoint', 'link'].includes(this.dragInfo.marker.name)
            ) {
                this.markersGroup.remove(this.dragInfo.marker);
                this.setState({placement: false});
            } else if (this.dragInfo && this.dragInfo.marker && !this.dragInfo.moved) {
                if (this.dragInfo.name === 'button' && !this.props.readOnly) {
                    this.onInfopointCRUD(this.dragInfo.marker.name);
                } else {
                    const infopoint = this.getMarkerInfopoint(this.dragInfo.marker);
                    if (infopoint) {
                        const linkedRoom = this.getLinkedRoom(infopoint);
                        if (linkedRoom && this.props.onSelectRoom) {
                            this.props.onSelectRoom(linkedRoom.get('id'));
                        } else if (infopoint.get('text') && this.props.onShowInfo) {
                            this.props.onShowInfo({
                                title: infopoint.get('title'),
                                text: infopoint.get('text'),
                            });
                        }
                    }
                }
            } else if (this.dragInfo && this.dragInfo.marker && !this.props.readOnly) {
                const infopoint = this.getMarkerInfopoint(this.dragInfo.marker);
                if (infopoint) {
                    this.onInfopointDragged(
                        infopoint.get('id'),
                        this.dragInfo.state.x,
                        this.dragInfo.state.y,
                    );
                }
            }
            this.dragInfo = undefined;
            this.invalid = true;
        }
    }

    onPointerMove = (ev) => {
        const mousePoint = this.getMousePoint(ev);
        const {marker, uv} = this.hitTest(mousePoint);
        this.highlightMarker(marker, !this.dragInfo);
        if (this.dragInfo) {
            this.dragInfo.moved = true;
            if (this.dragInfo.marker && !this.props.readOnly) {
                this.dragInfo.state = uv.x >= 0 ?
                    uv :
                    (this.dragInfo.state ? this.dragInfo.state : new THREE.Vector2(1, 0));
                this.dragInfo.marker.currentUV = this.dragInfo.state;
                this.updateMarkerMatrix(this.dragInfo.marker);
            } else {
                if (this.isRoom) {
                    const factor = (0.2 - ((this.stage.position.z + 801) / 10000)) * Math.PI / 180;
                    const latMax = 85 * Math.PI / 180;
                    const lat = this.dragInfo.startState.x + (this.dragInfo.startMouse.y - mousePoint.y) * factor;
                    this.stage.rotation.x = Math.min(Math.max(lat, -latMax), latMax);
                    this.stage.rotation.y =
                        this.dragInfo.startState.y -
                        (mousePoint.x - this.dragInfo.startMouse.x) * factor;
                    if (this.props.fixBottom) {
                        this.bottomMesh.rotation.z = -this.stage.rotation.y;
                    }
                } else {
                    const factor = (this.camera.right * 2) / this.props.width;
                    const xMax = 500;
                    const yMax = 500;
                    const x = this.dragInfo.startState.x + (mousePoint.x - this.dragInfo.startMouse.x) * factor;
                    const y = this.dragInfo.startState.y - (mousePoint.y - this.dragInfo.startMouse.y) * factor;
                    this.stage.position.x = Math.min(Math.max(x, -xMax), xMax);
                    this.stage.position.y = Math.min(Math.max(y, -yMax), yMax);
                }
            }
            this.forceUpdate();
            this.invalid = true;
        }
    }

    togglePlacement = (e, type) => {
        this.setState(
            {
                placement: this.state.placement === type ? false : type,
            },
            () => {
                if (!this.state.placement) {
                    this.markersGroup.remove(this.dragInfo.marker);
                    this.dragInfo = undefined;
                    this.invalid = true;
                } else {
                    this.dragInfo = {marker: this.addMarker(this.state.placement, this.state.placement)};
                }
            },
        );
    }

    start = () => {
        if (this.frameId === null) {
            this.frameId = requestAnimationFrame(this.animate);
        }
    }

    stop = () => {
        cancelAnimationFrame(this.frameId);
        this.frameId = null;
    }

    animate = () => {
        requestAnimationFrame(this.animate);
        this.update();
    }

    update = () => {
        if (this.props.autoPlay || this.invalid) {
            if (this.props.autoPlay && !this.dragInfo) {
                this.stage.rotation.y += 0.05 * Math.PI / 180;
                if (this.props.fixBottom) {
                    this.bottomMesh.rotation.z = -this.stage.rotation.y;
                }
            }
            this.renderer.render(this.scene, this.camera);
            this.invalid = false;
        }
    }

    onInfopointCRUD = (entity, uParam, vParam) => {
        const edit = !(['infopoint', 'link'].includes(entity));
        const infopoint = edit ? this.getInfopointById(Number(entity)) : null;
        const type = edit ? (infopoint.get('linked_room_id') > 0 ? 'link' : 'infopoint') : entity;
        const u = edit ? infopoint.get('u') : uParam;
        const v = edit ? infopoint.get('v') : vParam;
        const createOrUpdate = edit ? actions.data.infopoint.edit : actions.data.infopoint.save;
        this.props.onShowStandardDialog({
            title: (type === 'link' ? i18n[language].linkpoint_title : i18n[language].infopoint_title) + ' ' + (edit ? i18n[language].edit_point_title : i18n[language].create_point_title),
            pickTitle: type === 'infopoint' ? {
                label: i18n[language].point_form_title_label,
                placeholder: i18n[language].point_form_title_placeholder,
                value: edit ? infopoint.get('title') : undefined,
            } : false,
            editText: type === 'infopoint' ? {
                label: i18n[language].point_form_text_label,
                placeholder: i18n[language].point_form_text_placeholder,
                value: edit ? infopoint.get('text') : undefined,
            } : false,
            pickRoom: type === 'link' ? {
                value: edit ? infopoint.get('linked_room_id') : undefined,
            } : false,
            pickSymbol: {
                value: edit ? infopoint.get('symbol') : (type === 'link' ? 'passway' : 'info'),
            },
            secondary: edit && {
                createAction: () => actions.data.infopoint.remove({infopoint_id: infopoint.get('id')}),
                variant: 'danger',
                label: i18n[language].delete_point_accept_label,
            },
            accept: {
                createAction: ({symbol, linked_room_id, title, text}) => createOrUpdate({
                    [this.isRoom ? 'room_id' : 'floor_id']: this.props.entity.get('id'),
                    infopoint_id: edit && infopoint.get('id'), u, v, symbol, linked_room_id, title, text,
                }),
                variant: 'success',
                label: edit ? i18n[language].edit_point_accept_label : i18n[language].create_point_accept_label,
            },
        });
    }

    onInfopointDragged = (infopoint_id, u, v) => {
        this.props.onInfopointEditAction({
            infopoint_id, u, v,
        });
    }

    renderLoading = () => {
        if (this.state.loadState === 'loaded') {
            return null;
        }
        return (
            <div
                className="d-flex flex-fill align-items-center justify-content-center"
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                {this.state.loadState === 'loading' &&
                    <Spinner
                        animation="border"
                    />
                }
                <div className="p-3">
                    <p className="text-center">
                        {
                            this.state.loadState === 'none' &&
                            (this.isFloor ?
                                i18n[language].no_floorplan_message :
                                i18n[language].no_image_message
                            )
                        }
                    </p>
                </div>

            </div>
        );
    }

    renderAddButton = (id, title, icon) =>
        <Button
            variant={this.state.placement === id ? 'danger' : 'success'}
            disabled={this.state.placement && this.state.placement !== id}
            onClick={(e) => this.togglePlacement(e, id)}
            className="shadow mr-1"
            title={title}
            style={{
                borderRadius: '50%',
                lineHeight: '1',
                padding: '0.5em',
            }}
        >
            <Icon
                icon={this.state.placement === id ? cancelIcon : icon}
                width="1.5em"
            />
        </Button>

    renderControls = () => {
        if (this.state.loadState !== 'loaded' || this.props.readOnly) {
            return null;
        }
        return (
            <>
                {
                    this.isRoom && <div
                        style={{
                            position: 'absolute',
                            bottom: 15,
                            left: 15,
                            zIndex: 100,
                        }}
                    >
                        <Button
                            variant="success"
                            disabled={this.isStartRotation()}
                            onClick={() => this.setStartRotation()}
                            className="shadow ml-1"
                            title={i18n[language].controls_set_start_rotation_label}
                            style={{
                                borderRadius: '50%',
                                lineHeight: '1',
                                padding: '0.5em',
                            }}
                        >
                            <Icon
                                icon={setDefaultRotationIcon}
                                width="1.5em"
                            />
                        </Button>
                        <Button
                            variant="success"
                            disabled={this.isStartRotation()}
                            onClick={() => this.gotoStartRotation()}
                            className="shadow ml-1"
                            title={i18n[language].controls_goto_start_rotation_label}
                            style={{
                                borderRadius: '50%',
                                lineHeight: '1',
                                padding: '0.5em',
                            }}
                        >
                            <Icon
                                icon={showDefaultRotationIcon}
                                width="1.5em"
                            />
                        </Button>
                    </div>
                }
                <div
                    style={{
                        position: 'absolute',
                        bottom: 15,
                        right: 15,
                        zIndex: 100,
                    }}
                >
                    {this.renderAddButton('link', i18n[language].controls_create_linked_room_label, createLinkedRoomIcon)}
                    {this.renderAddButton('infopoint', i18n[language].controls_create_infopoint_label, createInfopointIcon)}
                </div>
            </>
        );
    }

    render() {
        return (
            <div
                className="scene"
                ref={this.renderRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                }}
            >
                {this.renderControls()}
                {this.renderLoading()}
            </div>
        );
    }
}
